import { connect, FieldArray, FormikValues } from 'formik'
import { capitalize, flatten, get } from 'lodash'
import { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Toggle from 'react-toggle'
import styled from 'styled-components'
import { v4 } from 'uuid'

import Button from '@/components/Button'
import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import SelectComponent from '@/components/Form/Select'
import TextComponent from '@/components/Form/Text'
import { AddCircle, Delete } from '@/components/Icons'
import {
  StyledWandIcon,
  VariableButton,
  VariableInputContainer
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import operationsMessages from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/messages'
import {
  ActionBlockDataType,
  ActionTypes,
  FieldTypes,
  FlowElement,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import ActionDataBuilder from './ActionDataBuilder'
import messages from './messages'
import { AnyContentBlockTypes } from './NotificationDetail'
import { BlockBorder, BlockTitle, Label, StyledBlockTitleBar } from './StyledComponents'

const Select: any = SelectComponent
const Text: any = TextComponent

const styles = {
  [ActionTypes.ActionOpenUrl]: ['transparent'],
  [ActionTypes.ActionSubmit]: ['primary', 'secondary', 'transparent']
}
const submitType = [
  {
    displayTitle: messages.submit,
    initialData: {
      actionType: 'primary',
      type: ActionTypes.ActionSubmit,
      causesValidation: true,
      title: '',
      data: {
        placeholder: 'data'
      }
    }
  },
  {
    displayTitle: messages.url,
    initialData: {
      actionType: 'transparent',
      type: ActionTypes.ActionOpenUrl,
      title: '',
      url: ''
    }
  }
]

const StyledRow = styled.div`
  display: flex;
  flex: 1 0 auto;
  width: 100%;
  > div {
    padding-right: 10px;
  }
`

const StyledCentredRow = styled(StyledRow)`
  align-items: center;
  margin: 20px 0;
`

const StyledSelect = styled(Select).withConfig({
  shouldForwardProp: prop => prop !== 'isPlaceholder'
})`
  width: 100%;
  .field__wrapper .field__input {
    color: ${props => (props.isPlaceholder ? 'gray' : '#000000')};
  }
`

const StyledToggle = styled(Toggle)`
  && {
    padding-right: 0;
    margin-right: 10px;
  }
`

const UrlMentionsRichTextEditorWithFormik = styled(MentionsRichTextEditorWithFormik)`
  width: ${props => (props.mentions?.length > 0 ? '75%' : '100%')};
`

const StyledBlockBorder = styled(BlockBorder)`
  border-color: #cccccc7d;
  margin-top: 15px;
`
const ActionBlockContent = styled.div`
  margin-top: 10px;
`

interface AddButtonProps {
  onClick: () => void
  message: keyof typeof messages
  id?: string
}

export const AddButton = ({ onClick, message }: AddButtonProps) => {
  return (
    <Button color="transparent" onClick={onClick}>
      <AddCircle />
      <FormattedMessage {...messages[message]} />
    </Button>
  )
}

const DefaultOption = () => {
  const intl = useIntl()

  return (
    <option value={-1} disabled>
      {intl.formatMessage(messages.interactionTypePlaceholder)}
    </option>
  )
}

interface ButtonBlockProps {
  name: string
  selectedElement: FlowElement
  variables: Variable[][]
  readOnly?: boolean
}

const Component = connect(
  ({ name, formik, variables, selectedElement, readOnly = false }: ButtonBlockProps & FormikValues) => {
    const intl = useIntl()

    const formikActions = formik.values.stepData.notifications[0].actions
    const flattenedVariables = flatten(variables)
    const addVariableRef = useRef<any>(() => {})
    const [showVariablePopOver, setShowVariablePopOver] = useState<any>()

    const addBlock = (push: (obj: any) => void) => {
      const blockId = v4()
      const newActionBlock = { ...ButtonBlock.initialData, blockId }
      push({ ...newActionBlock, type: undefined })
    }

    const removeAll = () => {
      formik.setFieldValue(`stepData.notifications[0].actions`, [])
      formik.setFieldTouched(`stepData.notifications[0].actions`, [])
    }

    return (
      <FieldArray
        name={`stepData.notifications[0].actions`}
        render={arrayHelpers => (
          <BlockBorder>
            <StyledBlockTitleBar>
              <BlockTitle>{intl.formatMessage(messages.actionSet)}</BlockTitle>
              {!readOnly && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div className="icon remove" onClick={removeAll}>
                  <Delete title="Delete Action Set" />
                </div>
              )}
            </StyledBlockTitleBar>
            {formikActions?.map((action: ActionBlockDataType, i: number) => {
              return (
                <StyledBlockBorder key={action.blockId}>
                  <StyledBlockTitleBar>
                    <BlockTitle>{intl.formatMessage(messages.action, { index: i + 1 })}</BlockTitle>
                    {!readOnly && (
                      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                      <div className="icon remove" onClick={() => arrayHelpers.remove(i)}>
                        <Delete title="Delete Action" />
                      </div>
                    )}
                  </StyledBlockTitleBar>
                  <ActionBlockContent>
                    <StyledRow>
                      <StyledSelect
                        name={`${name}[${i}].type`}
                        defaultValue={-1}
                        isPlaceholder={!get(formik.values, `${name}[${i}].type`)}
                        label={intl.formatMessage(messages.interactionType)}
                        disabled={readOnly}
                        onChange={(e: any) => {
                          if (
                            e.target.value !== action.type &&
                            e.target.name.includes(`stepData.notifications[0].actions[${i}].type`)
                          ) {
                            const blockId = action.blockId
                            const title = action.title
                            const displayInSummary = action.displayInSummary
                            formik.setFieldValue(`stepData.notifications[0].actions[${i}]`, {
                              ...submitType.find(type => type.initialData.type === e.target.value)?.initialData,
                              title,
                              blockId,
                              displayInSummary: e.target.value === ActionTypes.ActionOpenUrl ? displayInSummary : false
                            })
                          }
                        }}
                      >
                        <>
                          <DefaultOption />
                          {submitType.map(typeOption => {
                            return (
                              <Select.Option
                                key={`${typeOption.initialData.type}${i}`}
                                value={typeOption.initialData.type}
                                data-testid={typeOption.initialData.type}
                              >
                                {intl.formatMessage(typeOption.displayTitle)}
                              </Select.Option>
                            )
                          })}
                        </>
                      </StyledSelect>
                    </StyledRow>
                    <StyledRow>
                      <StyledSelect
                        disabled={readOnly}
                        name={`${name}[${i}].actionType`}
                        label={intl.formatMessage(messages.style)}
                      >
                        {styles[get(action, `type`, ActionTypes.ActionOpenUrl)]?.map((styleOption: string) => (
                          <Select.Option key={styleOption} value={styleOption}>
                            {capitalize(styleOption)}
                          </Select.Option>
                        ))}
                      </StyledSelect>
                      <Text
                        name={`${name}[${i}].title`}
                        label={intl.formatMessage(messages.labelText)}
                        placeholder={intl.formatMessage(messages.addLabel)}
                        disabled={readOnly}
                        required
                      />
                    </StyledRow>
                    {action.type === ActionTypes.ActionOpenUrl && (
                      <VariableInputContainer>
                        <StyledRow>
                          <UrlMentionsRichTextEditorWithFormik
                            setAddMention={(addMentionFn: any) =>
                              (addVariableRef.current[`${name}[${i}].url`] = addMentionFn)
                            }
                            name={`${name}[${i}].url`}
                            mentions={getAllFlattenedVariables(flattenedVariables)}
                            placeholder={intl.formatMessage(messages.urlPlaceholder)}
                            label={intl.formatMessage(messages.url)}
                            required={true}
                            readOnly={readOnly}
                          />

                          {flattenedVariables.length > 0 && !readOnly && (
                            <VariableButton
                              onClick={() => {
                                setShowVariablePopOver(i)
                              }}
                            >
                              <StyledWandIcon title={intl.formatMessage(operationsMessages.addVariableButton)} />
                            </VariableButton>
                          )}
                        </StyledRow>
                        {showVariablePopOver === i && (
                          <VariableOverlay
                            onSelectVariable={(variable: Variable) =>
                              addVariableRef.current[`${name}[${i}].url`](variable)
                            }
                            onClose={() => {
                              setShowVariablePopOver(undefined)
                            }}
                            variables={variables}
                            validVariableTypes={[
                              FieldTypes.STRING,
                              FieldTypes.BOOLEAN,
                              FieldTypes.INTEGER,
                              FieldTypes.NUMBER,
                              FieldTypes.ANY
                            ]}
                          />
                        )}
                      </VariableInputContainer>
                    )}
                    {(action.type === ActionTypes.ActionOpenUrl || action.type === ActionTypes.ActionSubmit) && (
                      <StyledCentredRow>
                        <Label>{intl.formatMessage(messages.showOnSummary)}</Label>
                        <StyledToggle
                          disabled={readOnly}
                          checked={get(formik.values, `${name}[${i}].displayInSummary`)}
                          name={`${name}[${i}].displayInSummary`}
                          value={String(get(formik.values, `${name}[${i}].displayInSummary`))}
                          onChange={() =>
                            formik.setFieldValue(
                              `${name}[${i}].displayInSummary`,
                              !get(formik.values, `${name}[${i}].displayInSummary`)
                            )
                          }
                        />
                      </StyledCentredRow>
                    )}
                    {action.type === ActionTypes.ActionSubmit && (
                      <StyledCentredRow>
                        <Label>{intl.formatMessage(messages.enforceValidation)}</Label>
                        <StyledToggle
                          disabled={readOnly}
                          aria-label="causesValidaton"
                          checked={get(formik.values, `${name}[${i}].causesValidation`)}
                          name={`${name}[${i}].causesValidation`}
                          value={String(get(formik.values, `${name}[${i}].causesValidation`))}
                          onChange={() =>
                            formik.setFieldValue(
                              `${name}[${i}].causesValidation`,
                              !get(formik.values, `${name}[${i}].causesValidation`)
                            )
                          }
                        />
                      </StyledCentredRow>
                    )}
                  </ActionBlockContent>
                </StyledBlockBorder>
              )
            })}
            {formikActions.length < 3 && !readOnly && (
              <AddButton onClick={() => addBlock(arrayHelpers.push)} message={'addAction'} />
            )}
            {formikActions.find((action: ActionBlockDataType) => action.type === ActionTypes.ActionSubmit) && (
              <ActionDataBuilder variables={variables} selectedElement={selectedElement} readOnly={readOnly} />
            )}
          </BlockBorder>
        )}
      />
    )
  }
)

const ButtonBlock: AnyContentBlockTypes = { Component }

ButtonBlock.displayName = 'ButtonBlock'

ButtonBlock.initialData = submitType[0].initialData

export default ButtonBlock
